/**
 * Edit Subdomain Modal Component
 * Allows editing of subdomain metadata (description, scope)
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, AlertCircle, Loader2 } from 'lucide-react';

import { updateSubdomain } from '../../lib/api/domainPacksApi';

interface EditSubdomainModalProps {
  domainPackId: string;
  subdomain: {
    subdomain_id: string;
    subdomain_name: string;
    description?: string;
    scope?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  description: string;
  scope: string;
}

export const EditSubdomainModal: React.FC<EditSubdomainModalProps> = ({
  domainPackId,
  subdomain,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    description: subdomain.description || '',
    scope: subdomain.scope || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form when subdomain prop changes
  useEffect(() => {
    setFormData({
      description: subdomain.description || '',
      scope: subdomain.scope || '',
    });
  }, [subdomain]);

  // Mutation for updating subdomain
  const updateMutation = useMutation({
    mutationFn: () =>
      updateSubdomain(domainPackId, subdomain.subdomain_id, {
        description: formData.description || undefined,
        scope: formData.scope || undefined,
      }),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['domain-pack', domainPackId] });
      queryClient.invalidateQueries({ queryKey: ['domain-packs'] });
      handleClose();
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail || 'Failed to update subdomain';
      setErrors({ submit: detail });
    },
  });

  const handleClose = () => {
    setFormData({
      description: subdomain.description || '',
      scope: subdomain.scope || '',
    });
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (formData.scope && formData.scope.length > 500) {
      newErrors.scope = 'Scope must be less than 500 characters';
    }

    // Check if anything changed
    const descriptionChanged =
      formData.description !== (subdomain.description || '');
    const scopeChanged = formData.scope !== (subdomain.scope || '');

    if (!descriptionChanged && !scopeChanged) {
      newErrors.submit = 'No changes detected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    updateMutation.mutate();
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    // Clear submit error when user makes changes
    if (errors.submit) {
      setErrors({ ...errors, submit: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              Edit Subdomain
            </h2>
            <p className="text-sm text-neutral-600 mt-1">
              {subdomain.subdomain_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            disabled={updateMutation.isPending}
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">
              Description <span className="text-neutral-500">(optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of what this subdomain covers..."
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                errors.description ? 'border-red-500' : 'border-neutral-300'
              }`}
              disabled={updateMutation.isPending}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                Help the AI understand the focus of this subdomain
              </p>
              <span className="text-xs text-neutral-500">
                {formData.description.length}/500
              </span>
            </div>
            {errors.description && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-2">
              Scope <span className="text-neutral-500">(optional)</span>
            </label>
            <textarea
              value={formData.scope}
              onChange={(e) => handleInputChange('scope', e.target.value)}
              placeholder="Define the boundaries and coverage of this subdomain..."
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                errors.scope ? 'border-red-500' : 'border-neutral-300'
              }`}
              disabled={updateMutation.isPending}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                What's included and what's excluded?
              </p>
              <span className="text-xs text-neutral-500">
                {formData.scope.length}/500
              </span>
            </div>
            {errors.scope && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.scope}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">
                  {errors.submit === 'No changes detected'
                    ? 'No changes detected'
                    : 'Failed to update subdomain'}
                </p>
                <p className="text-sm text-red-700 mt-1">{errors.submit}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
